import React, { useEffect, useState } from 'react';
import { fetchCatalogHistory } from '../api';
import type { AuditLogItem, VersionHistoryItem } from '../types';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRollback: (targetVersion: number, comment: string) => void;
}

export const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  onRollback,
}) => {
  const [versions, setVersions] = useState<VersionHistoryItem[]>([]);
  const [audits, setAudits] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'versions' | 'audit'>('versions');
  const [rollbackComment, setRollbackComment] = useState('');
  const [targetVersion, setTargetVersion] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchCatalogHistory()
      .then((data) => {
        setVersions(data.versions);
        setAudits(data.audits);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="cov-modal-overlay" onClick={onClose}>
      <div className="cov-modal-card" style={{ maxWidth: '720px' }} onClick={(e) => e.stopPropagation()}>
        <div className="cov-modal-head">
          <div>
            <h3 style={{ margin: 0, fontSize: '1.125rem' }}>История версий и аудит</h3>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Все опубликованные манифесты и действия администраторов
            </span>
          </div>
          <button className="cov-btn cov-btn-outline" onClick={onClose}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1.5rem 0', borderBottom: '1px solid #1e293b' }}>
          <button
            className={`cov-tab-btn ${activeTab === 'versions' ? 'active' : ''}`}
            onClick={() => setActiveTab('versions')}
          >
            Версии ({versions.length})
          </button>
          <button
            className={`cov-tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Журнал аудита ({audits.length})
          </button>
        </div>

        <div className="cov-modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Загрузка истории...</div>
          ) : activeTab === 'versions' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {versions.map((v, idx) => (
                <div
                  key={v.id}
                  style={{
                    background: '#131d31',
                    border: '1px solid #1e293b',
                    borderRadius: '8px',
                    padding: '0.875rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <strong style={{ fontSize: '1rem', color: '#38bdf8' }}>v{v.version_number}</strong>
                      {idx === 0 && <span className="cov-role-badge cov-badge-allowed">Текущая активная</span>}
                      <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {v.published_at ? new Date(v.published_at).toLocaleString('ru-RU') : 'Черновик'}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: '#cbd5e1' }}>
                      {v.comment || 'Без комментария'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                      Автор: {v.created_by}
                    </div>
                  </div>

                  {idx !== 0 && (
                    <button
                      className="cov-btn cov-btn-outline"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => setTargetVersion(v.version_number)}
                    >
                      ↺ Откатить к v{v.version_number}
                    </button>
                  )}
                </div>
              ))}

              {targetVersion !== null && (
                <div style={{ background: '#1c1917', border: '1px solid #f59e0b', borderRadius: '8px', padding: '1rem', marginTop: '0.5rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#f59e0b' }}>
                    Подтверждение отката к версии v{targetVersion}
                  </h4>
                  <p style={{ fontSize: '0.8125rem', color: '#d6d3d1', margin: '0 0 0.5rem 0' }}>
                    Будет создана новая версия, точно копирующая состояние v{targetVersion}.
                  </p>
                  <input
                    className="cov-form-input"
                    style={{ width: '100%', marginBottom: '0.5rem' }}
                    placeholder="Причина отката..."
                    value={rollbackComment}
                    onChange={(e) => setRollbackComment(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button className="cov-btn cov-btn-outline" onClick={() => setTargetVersion(null)}>Отмена</button>
                    <button
                      className="cov-btn cov-btn-primary"
                      onClick={() => {
                        onRollback(targetVersion, rollbackComment);
                        setTargetVersion(null);
                        onClose();
                      }}
                    >
                      Выполнить откат
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {audits.map((a) => (
                <div
                  key={a.id}
                  style={{
                    background: '#131d31',
                    border: '1px solid #1e293b',
                    borderRadius: '6px',
                    padding: '0.625rem 0.875rem',
                    fontSize: '0.75rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                    <strong style={{ color: '#f59e0b' }}>{a.action}</strong>
                    <span style={{ color: '#64748b' }}>{new Date(a.timestamp).toLocaleString('ru-RU')}</span>
                  </div>
                  <div style={{ color: '#cbd5e1' }}>
                    {a.actor_name} ({a.role}) · {a.object_type}: {a.object_id} · Версия v{a.catalog_version}
                  </div>
                  {a.comment && <div style={{ color: '#94a3b8', fontStyle: 'italic', marginTop: '0.2rem' }}>{a.comment}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cov-modal-foot">
          <button className="cov-btn cov-btn-outline" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
};
